import styled from '@emotion/styled'
import { FormControlLabel, Radio, Typography } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { Plus } from '@emotion-icons/fa-solid/Plus'
import { Delete } from '@emotion-icons/material/Delete'
import { Colors, Shapes } from 'src/components/theme'
import * as billingAtoms from 'src/atoms/billing'
import { useRecoilRefresher_UNSTABLE, useRecoilValue, useSetRecoilState } from 'recoil'
import { CREADIT_CARDS_NAMES } from 'src/constants/credit-cards'
import { Button } from 'src/components/button'
import { CreditCardForm } from 'src/views/settings/billing/credit-card-form'
import { billingClient } from 'src/clients'
import { Alert } from 'src/components/alert/alert'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { LabelExtra } from 'src/views/subscriptions/table'
import { BillingPaymentMethod } from 'src/common-services'
import { ScrollableContainer } from 'src/components/scrollable-container'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  max-width: 550px;
`

const CardInfo = styled.div`
  flex: 1;
  display: flex;
  gap: 40px;
  align-items: center;
  padding-block: ${Shapes.spacing[2]}px;
`

const CardMainInfo = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 220px;
  max-width: 220px;
`
const CardName = styled.span`
  display: flex;
  align-items: center;
  gap: ${Shapes.spacing[1]}px;
`
const ExpirationText = styled(Typography)`
  min-width: 95px;
  max-width: 95px;
`

const DeleteErrorWrapper = styled.div`
  max-width: 520px;
`
const AddButton = styled(ButtonWithIcon)`
  text-decoration: underline;
  padding-inline: ${Shapes.spacing[1]}px;
  :hover {
    box-shadow: none;
    background-color: transparent;
  }
`

const EditText = styled(Typography)`
  text-decoration: underline;
  cursor: pointer;
`

const Actions = styled.div`
  flex: 1;
  display: flex;
  gap: 10px;
`

const DefaultChip = styled(LabelExtra)``
const StyledFormControlLabel = styled(FormControlLabel)`
  display: flex;
  padding: 0;
  margin: 0;
  margin-left: -${Shapes.spacing[2]}px;
  border-bottom: 1px solid ${Colors.border};
  :last-of-type {
    border-bottom: none;
  }
  :hover {
    background-color: ${Colors.neutral50};
  }
`

type DeleteConfirmDialogProps = {
  open: boolean
  onClose: () => void
  onConfirm: () => void
}

export const ConfirmDeleteDialog: FC<DeleteConfirmDialogProps> = ({ open, onClose, onConfirm }) => {
  return (
    <Dialog open={open} keepMounted onClose={onClose}>
      <DialogTitle>Are you sure you want to delete this credit card?</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-slide-description">This action cannot be undone.</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={onConfirm} color="secondary">
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const formatCardExpiration = (paymentMethod: BillingPaymentMethod) => {
  const month = paymentMethod.expMonth.toString().padStart(2, '0')
  const year = paymentMethod.expYear.toString().slice(-2)
  return `${month}/${year}`
}

type PaymentMethodsProps = {
  hideDelete?: boolean // Hides the ability to delete a payment method
  defaultAsRadio?: boolean // Renders the default payment method as a radio button label
  cardListMaxHeight?: number // Max height of the card list for scrolling
}
export const PaymentMethods: FC<PaymentMethodsProps> = (props) => {
  const { hideDelete, defaultAsRadio, cardListMaxHeight } = props
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState(false)
  const [successAdd, setSuccessAdd] = useState(false)
  const paymentMethods = useRecoilValue(billingAtoms.paymentMethods)
  const refreshPaymentMethods = useRecoilRefresher_UNSTABLE(billingAtoms.paymentMethods)
  const setIsChangePaymentMethodComplete = useSetRecoilState(billingAtoms.isChangePaymentMethodComplete)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [pmToDelete, setPmToDelete] = useState<string>()
  const [internalDefaultPaymentMethodId, setInternalDefaultPaymentMethodId] = useState<string>()

  // Sync default payment method with internal state
  useEffect(() => {
    if (!paymentMethods) {
      return
    }
    const defaultPaymentMethod = paymentMethods.find((pm) => pm.default)
    if (defaultPaymentMethod) {
      setInternalDefaultPaymentMethodId(defaultPaymentMethod.id)
    }
  }, [paymentMethods])

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError(undefined)
      }, 3000)
    }
  }, [error, setError])

  useEffect(() => {
    if (successAdd) {
      setTimeout(() => {
        setSuccessAdd(false)
      }, 3000)
    }
  }, [successAdd, setSuccessAdd])

  const onAddSuccess = () => {
    setOpen(false)
    refreshPaymentMethods()
    setSuccessAdd(true)
  }

  const performDelete = async () => {
    if (loading || !pmToDelete) {
      return
    }
    setConfirmDeleteOpen(false)
    setLoading(true)
    try {
      await billingClient.deletePaymentMethod(pmToDelete)
      setPmToDelete(undefined)
      refreshPaymentMethods()
    } catch {
      setError('Failed to delete payment method')
    } finally {
      setLoading(false)
    }
  }

  const onClickDelete = async (id: string) => {
    setPmToDelete(id)
    setConfirmDeleteOpen(true)
  }

  const onSetDefault = async (id: string) => {
    if (loading) {
      return
    }
    setLoading(true)
    try {
      await billingClient.setDefaultPaymentMethod(id)
      refreshPaymentMethods()
    } catch {
      setError('Failed to set default payment method')
    } finally {
      setLoading(false)
    }
  }

  const onRadioDefaultChange = (id: string) => async () => {
    setIsChangePaymentMethodComplete(false)
    setInternalDefaultPaymentMethodId(id)
    await onSetDefault(id)
    setIsChangePaymentMethodComplete(true)
  }

  const getCardInfo = (paymentMethod: BillingPaymentMethod, index: number) => {
    return (
      <CardInfo id={paymentMethod.id}>
        <CardMainInfo>
          <CardName>
            {paymentMethod.brand !== 'unknown' && <img src={`/credit-cards/${paymentMethod.brand}.png`} height={20} />}
            <Typography color={Colors.neutral950} variant="textmd" noWrap>{`${
              CREADIT_CARDS_NAMES[paymentMethod.brand]
            }`}</Typography>
          </CardName>
          <Typography color={Colors.neutral950} variant="textmd" noWrap>{`•••• ${paymentMethod.last4}`}</Typography>
        </CardMainInfo>

        <ExpirationText color={Colors.neutral500} variant="textmdm">
          {`Expires: ${formatCardExpiration(paymentMethod)}`}
        </ExpirationText>

        <Actions>
          {!defaultAsRadio && (
            <>
              {paymentMethod.default ? (
                <DefaultChip color="default" label="Default" />
              ) : (
                <EditText color={Colors.blueDark} variant="textmdm" onClick={() => onSetDefault(paymentMethod.id)}>
                  Set as default
                </EditText>
              )}
            </>
          )}
          {!hideDelete && !paymentMethod.default && (
            <Delete size={20} color={Colors.redDark} cursor="pointer" onClick={() => onClickDelete(paymentMethod.id)} />
          )}
        </Actions>
      </CardInfo>
    )
  }

  return (
    <>
      <Container>
        {error && (
          <DeleteErrorWrapper>
            <Alert message={error} color="error" />
          </DeleteErrorWrapper>
        )}
        {successAdd && (
          <DeleteErrorWrapper>
            <Alert message="Credit Card added" color="success" />
          </DeleteErrorWrapper>
        )}
        <ScrollableContainer height={cardListMaxHeight}>
          {paymentMethods?.map((paymentMethod, index) => {
            if (defaultAsRadio) {
              return (
                <StyledFormControlLabel
                  key={paymentMethod.id}
                  checked={internalDefaultPaymentMethodId === paymentMethod.id}
                  control={<Radio />}
                  label={getCardInfo(paymentMethod, index)}
                  onChange={onRadioDefaultChange(paymentMethod.id)}
                />
              )
            }

            return getCardInfo(paymentMethod, index)
          })}
        </ScrollableContainer>
        <AddButton ghost onClick={() => setOpen(true)}>
          <Plus size={14} />
          Add new card
        </AddButton>
      </Container>
      <CreditCardForm open={open} onClose={() => setOpen(false)} onSuccess={onAddSuccess} />
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={performDelete}
      />
    </>
  )
}
